import { useMemo } from 'react'
import { useRemoteParticipants, useRoomContext } from '@livekit/components-react'
import { ConnectionState, type RoomEvent } from 'livekit-client'
import { useEventLiveKit } from '@/components/livekit/EventLiveKitProvider'
import { personToneKey } from '@/lib/profile'
import { profileAvatarUrl } from './rules'
import type { StageFace } from './types'

/** Re-render on arrivals, departures and the connection only, not on every participant's media events. */
export const MEMBERSHIP_ONLY: RoomEvent[] = []

/** The most people the online tray lists one by one; the rest are "+N more" (a big audience stays cheap to draw). */
export const ONLINE_ROWS = 200

/** The avatars the collapsed tray overlaps before "+N". */
export const ONLINE_STACK = 5

/**
 * The most seats the audience draws (docs/features/watch-together.md); the rest is one "+N" seat. Enough to feel the
 * crowd, few enough that thousands watching stay cheap to draw.
 */
export const AUDIENCE_SEATS = 400

/**
 * Who a watcher is, so a person counts once however many tabs they have open: a watch token's `u_<user>.<tab>` is
 * `u_<user>`, an anonymous `anon_<uuid>` is itself (the old viewers' tokens too), as the API's webhook tells them apart
 * (`LivekitWebhookService::WATCH_IDENTITY`). Null for everyone else in the room: cameras, their telemetry and LiveKit's
 * own agents are participants, but not audience.
 */
export function watcherKey(identity: string | null | undefined): string | null {
  if (!identity) return null
  if (identity.startsWith('u_')) {
    const tab = identity.lastIndexOf('.')
    return tab > 2 ? identity.slice(0, tab) : identity
  }
  return identity.startsWith('anon_') ? identity : null
}

/** How many other people are watching: the distinct watchers among `identities`, leaving out `self`'s own tabs. */
export function othersWatching(identities: readonly string[], self: string | null | undefined): number {
  const own = watcherKey(self)
  const others = new Set<string>()
  for (const identity of identities) {
    const key = watcherKey(identity)
    if (key && key !== own) others.add(key)
  }
  return others.size
}

/** A room participant as the tray reads it: what LiveKit has from the API's token (`ViewerIdentity`). */
export interface OnlineParticipant {
  identity: string
  metadata?: string | null
}

/** Someone online, as the tray lists them. */
export interface OnlinePerson {
  /** Who they are across their tabs (`watcherKey`). */
  key: string
  /** What the tray calls them: a live host's name as the stage has it, else their handle; null without either. */
  name: string | null
  /** Their public handle (the token's `username`); null for an anonymous viewer or an account without one. */
  handle: string | null
  /** Their profile photo (the token's `avatar_url`), an absolute http(s) URL; null without one. */
  avatarUrl: string | null
  /** What their avatar's tone is keyed on, as in the chat and on the faces (`personToneKey`); null: the neutral disc. */
  toneKey: string | null
  /** This viewer (any of their tabs). */
  you: boolean
  /** Live as a host on the stage on screen: one of its host seats is one of their connections. */
  host: boolean
}

/** Who is online in the event's room, as the tray shows it. */
export interface Online {
  /** People online, this viewer included: a signed-in account once however many tabs, each anonymous viewer once. */
  count: number
  /**
   * The rows: the stage's live hosts in their seats' order, this viewer, then everyone else with a handle by name;
   * at most `ONLINE_ROWS` (the hosts and this viewer always among them).
   */
  people: OnlinePerson[]
  /** People with a handle past the rows (the tray's "+N more"). */
  more: number
  /** Everyone else with no name to show: anonymous viewers and accounts without a handle (the tray's one "N guests"). */
  guests: number
  /**
   * The audience's seats, everyone once, guests too (a neutral disc): the stage's live hosts, this viewer, then everyone
   * else newest arrival first, so someone arriving takes the front row. At most `AUDIENCE_SEATS`; the rest is
   * `count - audience.length`.
   */
  audience: OnlinePerson[]
}

/** A disc in the collapsed tray: a person, or an anonymous viewer as the neutral glyph. */
export type OnlineStackItem = { kind: 'person'; person: OnlinePerson } | { kind: 'guest'; key: string }

const NO_PROFILE = { handle: null, avatarUrl: null }

/** The handle and photo in a participant's metadata; nothing for metadata that isn't the API's JSON object. */
function profileOf(metadata: string | null | undefined): { handle: string | null; avatarUrl: string | null } {
  if (!metadata) return NO_PROFILE
  let parsed: unknown
  try {
    parsed = JSON.parse(metadata)
  } catch {
    return NO_PROFILE
  }
  if (!parsed || typeof parsed !== 'object') return NO_PROFILE
  const { username, avatar_url: avatarUrl } = parsed as Record<string, unknown>
  return {
    handle: typeof username === 'string' && username.trim() ? username.trim() : null,
    avatarUrl: typeof avatarUrl === 'string' ? profileAvatarUrl(avatarUrl) : null,
  }
}

const byName = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true })

interface Draft {
  key: string
  handle: string | null
  avatarUrl: string | null
  /** The earliest host seat among their connections (the stage's order), null when none is a host's. */
  seat: number | null
  seatName: string | null
}

/**
 * Who is online, from this tab's participant and the room's others (`self` and `remote`) and the stage's live seats
 * (`members`, the room metadata's): each watcher once (`watcherKey`), their handle and photo from any of their tabs, a
 * host when one of their connections holds one of the stage's host seats. A signed-in account without a handle has no
 * name to show, so it counts with the guests. Cameras and agents aren't listed.
 */
export function onlineFrom({
  self,
  remote,
  members,
  rows = ONLINE_ROWS,
  seats: seatCount = AUDIENCE_SEATS,
}: {
  self: OnlineParticipant | null
  /** The room's other participants in the order they joined (LiveKit's). */
  remote: readonly OnlineParticipant[]
  members: readonly StageFace[]
  rows?: number
  seats?: number
}): Online {
  const seats = new Map<string, { index: number; name: string | null }>()
  members.forEach((member, index) => {
    if (member.role === 'host') seats.set(member.identity, { index, name: member.name })
  })
  const drafts = new Map<string, Draft>()
  const add = (participant: OnlineParticipant) => {
    const key = watcherKey(participant.identity)
    if (!key) return
    const draft = drafts.get(key) ?? { key, handle: null, avatarUrl: null, seat: null, seatName: null }
    // Only a signed-in connection carries a profile; an anonymous one is nobody in particular.
    const profile = key.startsWith('u_') ? profileOf(participant.metadata) : NO_PROFILE
    draft.handle ??= profile.handle
    draft.avatarUrl ??= profile.avatarUrl
    const seat = seats.get(participant.identity)
    if (seat && (draft.seat === null || seat.index < draft.seat)) {
      draft.seat = seat.index
      draft.seatName = seat.name
    }
    drafts.set(key, draft)
  }
  if (self) add(self)
  remote.forEach(add)

  const selfKey = watcherKey(self?.identity)
  const person = (draft: Draft): OnlinePerson => ({
    key: draft.key,
    name: (draft.seat !== null && draft.seatName?.trim()) || draft.handle,
    handle: draft.handle,
    avatarUrl: draft.avatarUrl,
    toneKey: personToneKey({ handle: draft.handle, id: draft.key.startsWith('u_') ? draft.key.slice(2) : null }),
    you: draft.key === selfKey,
    host: draft.seat !== null,
  })
  const hosts: Draft[] = []
  const named: Draft[] = []
  let you: Draft | null = null
  let guests = 0
  for (const draft of drafts.values()) {
    if (draft.seat !== null) hosts.push(draft)
    else if (draft.key === selfKey) you = draft
    else if (draft.handle) named.push(draft)
    else guests += 1
  }
  hosts.sort((a, b) => (a.seat ?? 0) - (b.seat ?? 0))
  named.sort((a, b) => byName.compare(a.handle ?? '', b.handle ?? ''))
  const leading = [...hosts, ...(you ? [you] : [])]
  const shown = named.slice(0, Math.max(0, rows - leading.length))
  // Drafts are in joining order (this viewer first, then the room's): newest first after the hosts and this viewer.
  const crowd: Draft[] = []
  for (const draft of [...drafts.values()].reverse()) {
    if (crowd.length >= Math.max(0, seatCount - leading.length)) break
    if (draft.seat === null && draft.key !== selfKey) crowd.push(draft)
  }
  return {
    count: drafts.size,
    people: [...leading, ...shown].map(person),
    more: named.length - shown.length,
    guests,
    audience: [...leading, ...crowd].slice(0, seatCount).map(person),
  }
}

/**
 * The collapsed tray's discs: the first rows, then anonymous viewers' neutral glyphs while there's room, up to `size`.
 * The rest is "+N" (`online.count` less these).
 */
export function onlineStack(online: Online, size = ONLINE_STACK): OnlineStackItem[] {
  const items: OnlineStackItem[] = online.people.slice(0, size).map((person) => ({ kind: 'person', person }))
  for (let index = 0; items.length < size && index < online.guests; index++) items.push({ kind: 'guest', key: `guest-${index}` })
  return items
}

/**
 * Who is online in the event's room (`onlineFrom`), while it is connected (a signal resume counts, as for the canvas
 * and `useAudience`); null before then and once it's lost, so nothing shows that isn't there. `members` are the stage's
 * live seats (the room metadata's), for its hosts. It re-renders on every arrival and departure, not on a participant's
 * media or metadata events, so call it in the small part that shows it. Use inside `WatchRoom`.
 */
export function useOnline(members: readonly StageFace[]): Online | null {
  const { connectionState } = useEventLiveKit()
  const room = useRoomContext()
  const remote = useRemoteParticipants({ updateOnlyOn: MEMBERSHIP_ONLY })
  const connected = connectionState === ConnectionState.Connected || connectionState === ConnectionState.SignalReconnecting
  const local = room.localParticipant
  const selfIdentity = local?.identity ?? ''
  const selfMetadata = local?.metadata ?? null
  return useMemo(
    () => (connected ? onlineFrom({ self: selfIdentity ? { identity: selfIdentity, metadata: selfMetadata } : null, remote, members }) : null),
    [connected, selfIdentity, selfMetadata, remote, members],
  )
}
